import { analytics } from '@/features/analytics/events'
import { Button } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { CheckCircle } from 'lucide-react'
import { useEffect, useRef } from 'react'

export type WizardStepProps = {
  projectId: string
  index: number
  title: string
  description?: React.ReactNode | string
  active: boolean
  completed: boolean
  buttonType: 'none' | 'default'
} & (
  | { buttonType: 'none' }
  | {
      buttonType: 'default'
      buttonLabel: string
      buttonDisabled?: boolean
      buttonLoading?: boolean
      completedLabel?: string
      onClick: () => void
    }
)

export const WizardStep = ({
  projectId,
  index,
  title,
  description,
  active,
  completed,
  ...buttonProps
}: WizardStepProps) => {
  const rootRef = useRef<HTMLDivElement>(null)

  const handleClick = () => {
    analytics.missionOnboardingStepCTA(projectId, title)
    if (buttonProps.buttonType === 'default') {
      buttonProps.onClick()
    }
  }

  useEffect(() => {
    if (!active) return
    if (document.querySelector('[data-slot="dialog-content"][data-state="open"]')) return
    const button = rootRef.current?.querySelector<HTMLButtonElement>('.wizard-cta-button')
    if (!button) return
    button.focus({ preventScroll: true })
    const rect = button.getBoundingClientRect()
    const inView = rect.top >= 0 && rect.bottom <= window.innerHeight
    if (!inView) button.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [active])

  return (
    <div ref={rootRef} className="relative flex items-start gap-4">
      <div className="max-mobile:absolute flex flex-col items-center self-stretch">
        <div
          className={clsx(
            'flex size-10 shrink-0 items-center justify-center rounded-full border font-semibold text-white',
            {
              'border-green': completed,
              'border-orange': !completed
            }
          )}
        >
          {index}
        </div>
      </div>
      <div className={clsx('flex flex-1 flex-col items-start gap-2 pb-8')}>
        <div className={clsx('text-subtitle text-gray-70 max-mobile:ml-14 max-mobile:my-2 font-semibold')}>{title}</div>
        {description && <div className="text-gray-80 text-left">{description}</div>}
        {buttonProps.buttonType === 'default' && (
          <div className="max-mobile:w-full mt-1">
            <Button
              size="small"
              variant={!completed ? 'default' : 'tertiary'}
              isDisabled={buttonProps.buttonDisabled}
              isLoading={buttonProps.buttonLoading}
              onClick={handleClick}
              className={clsx('wizard-cta-button max-mobile:w-full flex gap-2', {
                'text-green pointer-events-none': completed
              })}
            >
              {!completed ? (
                buttonProps.buttonLabel
              ) : (
                <>
                  <CheckCircle size={20} />{' '}
                  {buttonProps.completedLabel ? buttonProps.completedLabel : <Trans>Done</Trans>}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
